<template>
  <sidepanel
    v-if="user"
    id="userinfo-panel"
    :title="$t('permissions:viewUser.panel.title')"
    :processing="processing"
    :color-b-g="user.isInternal ? '#32c9a8' : '#7b35bd'"
    :color-f-g="user.isInternal ? '#ffffff' : '#ffffff'"
  >
    <UserInfo
      :user-id="user.userId"
      :user-name="user.permission.name"
      :user-email="user.permission.email"
      :user-phone="user.permission.phone"
      :label="$t('permissions:roles.' + user.permission.role)"
      :is-internal="user.isInternal"
      @loaded="processing = false"
    />
    <div class="panel-actions">
      <button
        class="button red outline"
        type="button"
        :disabled="processing || revealing"
        @click="removePermission"
      >
        {{ $t('permissions:viewUser.button.removePermission') }}
      </button>
    </div>
    <ConfirmDialog
      :active="removePermissionConfirmDialog.isRevealed"
      :title="$t('permissions:removePermission.confirmationTitle')"
      :confirm-label="$t('permissions:removePermission.confirmationConfirm')"
      :cancel-label="$t('permissions:removePermission.confirmationCancel')"
      @cancel="removePermissionConfirmDialog.cancel"
      @confirm="removePermissionConfirmDialog.confirm"
    >
      <p>{{ $t('permissions:removePermission.confirmation') }}</p>
      <template v-if="activeAssignments.length > 0">
        <p>
          {{ $t('permissions:removePermission.assignmentsWarning') }}
        </p>
        <ul>
          <li
            v-for="assignment in activeAssignments"
            :key="assignment.id"
            style="margin-bottom: 0.5em"
          >
            <strong>
              {{
                $t('permissions:removePermission.assignmentItem', {
                  count: assignment.incompleteTaskIds.length,
                })
              }}
            </strong>
            <ul v-if="assignment.groupedTasks.length > 0">
              <li v-for="group in assignment.groupedTasks" :key="group.label">
                {{
                  group.count > 1
                    ? `${group.label} (${group.count})`
                    : group.label
                }}
              </li>
              <li v-if="assignment.remainingTasks > 0">
                {{
                  $t('permissions:removePermission.assignmentMore', {
                    count: assignment.remainingTasks,
                  })
                }}
              </li>
            </ul>
          </li>
        </ul>
      </template>
    </ConfirmDialog>
  </sidepanel>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import { useConfirmDialog } from '@vueuse/core'
import { reactive } from 'vue'
import UserInfo from '../UserInfo.vue'

export default {
  components: {
    ConfirmDialog,
    UserInfo,
  },
  props: {
    user: {
      type: Object,
      required: false,
      default: null,
    },
    itemId: {
      type: String,
      required: true,
    },
    itemType: {
      type: String,
      required: true,
    },
  },
  setup() {
    // reactive() so nested refs (e.g. isRevealed) unwrap correctly in the
    // template and via `this`.
    const removePermissionConfirmDialog = reactive(useConfirmDialog())
    return { removePermissionConfirmDialog }
  },
  data() {
    return {
      processing: false,
      revealing: false,
      activeAssignments: [],
    }
  },
  computed: {
    permissionObject() {
      return this.$store.getters.permissionObject
    },
  },
  mounted() {
    this.processing = true
  },
  methods: {
    async removePermission() {
      // revealing is a re-entry lock held for the whole flow, including while
      // the confirm dialog awaits input. processing only drives the spinner
      // and is lowered during the dialog, so it can't guard against re-entry.
      if (this.processing || this.revealing) return
      this.revealing = true
      try {
        this.processing = true
        this.activeAssignments = []
        // Assignment revocation only applies to inspection permissions; other
        // item types have no assignments, so the warning check is skipped.
        const userEmail =
          this.user?.userDetails?.email || this.user?.permission?.email
        if (this.itemType === 'inspections' && userEmail) {
          const projectId = this.permissionObject?.projectId
          if (projectId) {
            const assignments = await this.$store.dispatch('fetchAssignments', {
              inspectionId: this.itemId,
              projectId,
            })
            const userAssignments = (assignments || []).filter(
              (a) =>
                a.payload?.details?.email?.toLowerCase() ===
                userEmail?.toLowerCase()
            )
            if (userAssignments.length > 0) {
              const perAssignmentIds = userAssignments.map((a) => [
                ...new Set(
                  (a.tasks || [])
                    .filter((t) => t.status === 'incomplete')
                    .map((t) => t.id)
                ),
              ])
              const captureIds = [...new Set(perAssignmentIds.flat())]
              const captures =
                captureIds.length > 0
                  ? await this.$store.dispatch('fetchCapturesByIds', {
                      captureIds,
                    })
                  : []
              const captureMap = Object.fromEntries(
                captures.map((c) => [c.id, c])
              )
              this.activeAssignments = userAssignments.map((a, i) => {
                const incompleteTaskIds = perAssignmentIds[i]
                const incompleteTasks = incompleteTaskIds
                  .map((id) => captureMap[id])
                  .filter(Boolean)
                // Collapse captures that share a label (e.g. two
                // "Inventar > Bord") into one row with a count, preserving
                // first-seen order.
                const groups = []
                const byLabel = new Map()
                for (const capture of incompleteTasks) {
                  const label =
                    capture.tags && capture.tags.length
                      ? capture.tags.join(' > ')
                      : capture.note || capture.id
                  const existing = byLabel.get(label)
                  if (existing) {
                    existing.count += 1
                  } else {
                    const group = { label, count: 1 }
                    byLabel.set(label, group)
                    groups.push(group)
                  }
                }
                const groupedTasks = groups.slice(0, 10)
                const shownTaskCount = groupedTasks.reduce(
                  (sum, g) => sum + g.count,
                  0
                )
                return {
                  ...a,
                  // incompleteTaskIds is the source of truth for what gets
                  // revoked and for the count shown to the user. incompleteTasks
                  // holds the capture docs we could load, used only for detail.
                  incompleteTaskIds,
                  incompleteTasks,
                  // groupedTasks are the deduped labels shown in the dialog;
                  // remainingTasks covers tasks beyond the shown groups plus
                  // any ids whose capture docs couldn't be loaded.
                  groupedTasks,
                  remainingTasks: incompleteTaskIds.length - shownTaskCount,
                }
              })
            }
          } else {
            console.warn(
              'removePermission: no projectId on permissionObject, skipping assignment revocation check'
            )
          }
        }
        this.processing = false
        const { isCanceled } = await this.removePermissionConfirmDialog.reveal()
        if (!isCanceled) {
          this.processing = true
          this.trackEvent({
            category: 'Workflow',
            action: 'Revoke permission',
            itemType: this.itemType,
          })
          try {
            // Revoke captures first, then remove the permission. These two
            // dispatches are not atomic: if removePermissions fails after
            // revocation succeeds, the user is left with captures revoked but
            // the permission intact. Retrying is safe because revoke removes
            // the tasks server-side (and archives emptied assignments, which
            // are filtered out by archived == false on re-fetch), so
            // already-revoked ids are never recomputed into captureIds. A
            // fully-atomic operation would require a combined server mutation.
            const captureIds = [
              ...new Set(
                this.activeAssignments.flatMap((a) => a.incompleteTaskIds)
              ),
            ]
            if (captureIds.length > 0) {
              await this.$store.dispatch('revokeAssignmentCaptures', {
                inspectionId: this.itemId,
                captureIds,
              })
            }
            await this.$store.dispatch('removePermissions', {
              type: this.itemType,
              itemId: this.itemId,
              userIds: [this.user.userId],
            })
            this.processing = false
            this.removePanel()
          } catch (error) {
            this.processing = false
            this.$store.dispatch('addToast', {
              message: this.$t('permissions:removePermission.revokeFailed'),
              type: 'bad',
            })
            console.error(error)
          }
        }
      } catch (error) {
        // Unexpected failure in the fetch/reveal phase; clear the spinner so
        // the button doesn't stay permanently disabled, and tell the user.
        this.processing = false
        this.$store.dispatch('addToast', {
          message: this.$t('permissions:removePermission.operationFailed'),
          type: 'bad',
        })
        console.error(error)
      } finally {
        this.revealing = false
      }
    },
  },
}
</script>
